import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldX } from "lucide-react";
import { clearTokens } from "../utils/auth";
import { useMeStatus } from "../hooks/useGuardianStatus";

/**
 * /blocked — full-page interstitial served when the user is authoritatively
 * `blocked`. Login route also rejects blocked users, so this page is
 * usually reached via redirect from ProtectedRoute or after an active
 * session gets blocked while the tab is open.
 *
 * Uses `useMeStatus` to detect reactivation and auto-navigate back to
 * /dashboard without a manual refresh.
 */
export default function BlockedPage() {
  const navigate = useNavigate();
  const { data } = useMeStatus({ intervalMs: 20000 });

  // Ensure no local tokens persist so the user can't slip back into
  // authenticated routes with a stale JWT.
  useEffect(() => { clearTokens(); }, []);

  // Prefilled ticket deep-link so support reps see the block reason
  // without asking the user to explain.
  const contactHref = "/contact?ref=account_status&status=blocked"
    + (data?.user_id ? `&user=${encodeURIComponent(data.user_id)}` : "");

  // If admin reactivates while user waits on this page, bounce them home.
  useEffect(() => {
    if (data && data.account_status && data.account_status !== "blocked") {
      navigate("/dashboard", { replace: true });
    }
  }, [data, navigate]);

  return (
    <div
      data-testid="blocked-page"
      className="min-h-screen bg-divine-black flex items-center justify-center px-6"
    >
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto w-20 h-20 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
          <ShieldX className="w-10 h-10 text-red-400" />
        </div>
        <h1 className="text-3xl font-cinzel text-white">
          This account has been suspended
        </h1>
        <p className="text-white/60 text-sm leading-relaxed">
          If you believe this is a mistake, please reach out to our support
          team so we can review your case.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Link
            to={contactHref}
            data-testid="blocked-contact-link"
            className="px-5 py-2 rounded-lg bg-divine-gold text-divine-black text-sm font-semibold hover:opacity-90"
          >
            Contact Support
          </Link>
          <Link
            to="/"
            className="px-5 py-2 rounded-lg border border-white/15 text-white/70 text-sm hover:bg-white/5"
          >
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
